import { useEffect } from 'react'

export const useDemoPlayerScroll = ({
  clock,
  completedStep,
  contentRef,
  finalOfferVisible,
  introduction,
  screenRef,
  showRecalculation,
}: DemoPlayerScrollParams) => {
  useEffect(() => {
    const screen = screenRef.current
    const content = contentRef.current

    if (!screen || !content) {
      return
    }

    let previousTouchY: number | null = null
    let draggingScrollbar = false

    const markManualScroll = () => {
      clock.manualScrollUntil = performance.now() + 1200
    }
    const hasSummaryOverlay = completedStep !== null || finalOfferVisible
    const hasBlockingOverlay = introduction || showRecalculation
    const hasOverlay = hasSummaryOverlay || hasBlockingOverlay
    const canScroll = () => {
      const windowElement = screen.closest('.demo__window')
      const isFullscreen = windowElement === document.fullscreenElement

      return isFullscreen && !hasOverlay
    }
    const handleWheel = (event: WheelEvent) => {
      if (!canScroll()) {
        return
      }

      if (event.deltaY === 0 || event.ctrlKey) {
        return
      }

      markManualScroll()
      let multiplier = 1

      if (event.deltaMode === 1) {
        multiplier = 16
      } else if (event.deltaMode === 2) {
        multiplier = content.clientHeight
      }

      const target = event.target instanceof Element ? event.target : null
      const nestedContainer = target?.closest('.demo-player__modal-layer, [role="listbox"]')

      if (nestedContainer) {
        return
      }

      event.preventDefault()
      content.scrollTop += event.deltaY * multiplier
    }
    const handleTouchStart = (event: TouchEvent) => {
      previousTouchY = event.touches[0]?.clientY ?? null
    }
    const handleTouchMove = (event: TouchEvent) => {
      const hasTouchPosition = previousTouchY !== null && event.touches.length > 0

      if (!canScroll() || !hasTouchPosition) {
        return
      }

      const nextTouchY = event.touches[0].clientY
      const target = event.target instanceof Element ? event.target : null
      const nestedContainer = target?.closest('.demo-player__modal-layer, [role="listbox"]')

      markManualScroll()

      if (nestedContainer) {
        previousTouchY = nextTouchY
        return
      }

      event.preventDefault()
      content.scrollTop += (previousTouchY ?? nextTouchY) - nextTouchY
      previousTouchY = nextTouchY
    }
    const handlePointerDown = (event: PointerEvent) => {
      const isScrollbar = event.target === content &&
        event.clientX >= content.getBoundingClientRect().right - 20

      if (canScroll() && isScrollbar) {
        draggingScrollbar = true
        markManualScroll()
      }
    }
    const handlePointerMove = () => {
      if (draggingScrollbar) {
        markManualScroll()
      }
    }
    const handlePointerUp = () => {
      if (draggingScrollbar) {
        markManualScroll()
        draggingScrollbar = false
      }
    }
    screen.addEventListener('wheel', handleWheel, { capture: true, passive: false })
    screen.addEventListener('touchstart', handleTouchStart, { passive: true })
    screen.addEventListener('touchmove', handleTouchMove, { capture: true, passive: false })
    screen.addEventListener('pointerdown', handlePointerDown, true)
    window.addEventListener('pointermove', handlePointerMove, true)
    window.addEventListener('pointerup', handlePointerUp, true)
    window.addEventListener('pointercancel', handlePointerUp, true)

    return () => {
      screen.removeEventListener('wheel', handleWheel, true)
      screen.removeEventListener('touchstart', handleTouchStart)
      screen.removeEventListener('touchmove', handleTouchMove, true)
      screen.removeEventListener('pointerdown', handlePointerDown, true)
      window.removeEventListener('pointermove', handlePointerMove, true)
      window.removeEventListener('pointerup', handlePointerUp, true)
      window.removeEventListener('pointercancel', handlePointerUp, true)
    }
  }, [ clock, completedStep, contentRef, finalOfferVisible, introduction, screenRef, showRecalculation ])

}
